import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getInventoryRequests, createInventoryRequest, approveInventoryRequest, rejectInventoryRequest, fulfillInventoryRequest, cancelInventoryRequest, getItemTypes, getInventoryItems, getStockBatches } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Check, X, Send, ShoppingCart, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  fulfilled: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-200 text-slate-700',
};

const URGENCY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-red-100 text-red-700',
};

const InventoryRequests = ({ user, setUser }) => {
  const FIELD_ROLES = ['fse', 'tl', 'asm', 'state_head'];
  const APPROVER_ROLES = ['tl', 'asm', 'state_head', 'warehouse_manager', 'hr_manager', 'admin'];
  const FULFILLER_ROLES = ['admin', 'hr_manager', 'warehouse_manager', 'asm', 'state_head', 'tl'];
  const isApprover = APPROVER_ROLES.includes(user?.role);
  const isFulfiller = FULFILLER_ROLES.includes(user?.role);
  const isFieldUser = FIELD_ROLES.includes(user?.role);

  const [tab, setTab] = useState(isApprover ? 'pending_my_approval' : 'mine');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemTypes, setItemTypes] = useState([]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ item_type_id: '', quantity: 1, urgency: 'normal', reason: '', notes: '' });

  // Action dialogs
  const [acting, setActing] = useState(null);
  const [actionMode, setActionMode] = useState(null);  // 'approve' | 'reject' | 'fulfill'
  const [actionNotes, setActionNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  // Fulfillment state
  const [availableItems, setAvailableItems] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getInventoryRequests({ scope: tab });
      setRequests(r.data);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { getItemTypes().then(r => setItemTypes(r.data)).catch(() => {}); }, []);

  const submitCreate = async () => {
    try {
      await createInventoryRequest({
        ...createForm,
        quantity: parseInt(createForm.quantity) || 1,
      });
      toast.success('Request submitted');
      setCreateOpen(false);
      setCreateForm({ item_type_id: '', quantity: 1, urgency: 'normal', reason: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAction = async (req, mode) => {
    setActing(req);
    setActionMode(mode);
    setActionNotes('');
    setRejectReason('');
    setSelectedItemIds(new Set());
    setSelectedBatchId('');

    if (mode === 'fulfill') {
      if (req.tracking_mode === 'serialized') {
        try {
          const r = await getInventoryItems({ item_type_id: req.item_type_id, status: 'in_stock', limit: 1000 });
          setAvailableItems(r.data);
        } catch {}
      } else {
        try {
          const r = await getStockBatches({ item_type_id: req.item_type_id });
          setAvailableBatches((r.data || []).filter(b => b.quantity_remaining >= req.quantity));
        } catch {}
      }
    }
  };

  const submitAction = async () => {
    try {
      if (actionMode === 'approve') {
        await approveInventoryRequest(acting.id, { notes: actionNotes });
        toast.success('Request approved');
      } else if (actionMode === 'reject') {
        if (!rejectReason) { toast.error('Please provide a reason'); return; }
        await rejectInventoryRequest(acting.id, { rejected_reason: rejectReason });
        toast.success('Request rejected');
      } else if (actionMode === 'fulfill') {
        if (acting.tracking_mode === 'serialized') {
          if (selectedItemIds.size !== acting.quantity) {
            toast.error(`Please pick exactly ${acting.quantity} item(s)`); return;
          }
          await fulfillInventoryRequest(acting.id, { item_ids: Array.from(selectedItemIds), notes: actionNotes });
        } else {
          if (!selectedBatchId) { toast.error('Select a source batch'); return; }
          await fulfillInventoryRequest(acting.id, { batch_id: selectedBatchId, notes: actionNotes });
        }
        toast.success('Request fulfilled & items transferred');
      }
      setActing(null); setActionMode(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const doCancel = async (req) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await cancelInventoryRequest(req.id); toast.success('Cancelled'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleItem = (id) => {
    const s = new Set(selectedItemIds);
    if (s.has(id)) s.delete(id);
    else if (s.size < (acting?.quantity || 0)) s.add(id);
    setSelectedItemIds(s);
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inventory Requests</h1>
            <p className="text-sm text-slate-500">Request inventory from your manager. Chain: FSE → TL → ASM → State Head → Warehouse</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} size="sm" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
            {(isFieldUser || isApprover) && (
              <Button onClick={() => setCreateOpen(true)} data-testid="new-request-btn"><Plus className="h-4 w-4 mr-1" />New Request</Button>
            )}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="tab-mine">My Requests</TabsTrigger>
            {isApprover && <TabsTrigger value="pending_my_approval" data-testid="tab-pending-my-approval">Pending My Approval</TabsTrigger>}
            {(isApprover || isFulfiller) && <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>}
          </TabsList>

          <TabsContent value={tab}>
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 text-xs font-medium">Request</th>
                        <th className="text-left p-2 text-xs font-medium">Item</th>
                        <th className="text-right p-2 text-xs font-medium">Qty</th>
                        <th className="text-left p-2 text-xs font-medium">Requested By</th>
                        <th className="text-left p-2 text-xs font-medium">Urgency</th>
                        <th className="text-left p-2 text-xs font-medium">Status</th>
                        <th className="text-left p-2 text-xs font-medium">Created</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map(r => (
                        <tr key={r.id} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-mono text-xs text-indigo-700">{r.request_code}</td>
                          <td className="p-2">
                            <p className="font-medium">{r.item_type_name}</p>
                            <p className="text-xs text-slate-500">{r.tracking_mode}</p>
                          </td>
                          <td className="p-2 text-right font-semibold">{r.quantity}</td>
                          <td className="p-2 text-xs">
                            <p>{r.requested_by_name}</p>
                            <p className="text-slate-500 uppercase">{r.requested_by_role}</p>
                          </td>
                          <td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${URGENCY_COLORS[r.urgency] || ''} capitalize`}>{r.urgency}</span></td>
                          <td className="p-2"><span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[r.status] || ''} capitalize`}>{r.status}</span></td>
                          <td className="p-2 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                          <td className="p-2 text-right space-x-1">
                            {/* Approve / Reject (only if pending and current user can approve) */}
                            {r.status === 'pending' && isApprover && r.requested_by_user_id !== user?.id && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => openAction(r, 'approve')} data-testid={`approve-${r.id}`}><Check className="h-3 w-3" /></Button>
                                <Button size="sm" variant="outline" onClick={() => openAction(r, 'reject')} data-testid={`reject-${r.id}`}><X className="h-3 w-3 text-red-500" /></Button>
                              </>
                            )}
                            {/* Fulfill (only approved, by fulfiller) */}
                            {r.status === 'approved' && isFulfiller && (
                              <Button size="sm" variant="default" onClick={() => openAction(r, 'fulfill')} data-testid={`fulfill-${r.id}`}><Send className="h-3 w-3 mr-1" />Fulfill</Button>
                            )}
                            {/* Cancel by requester */}
                            {(r.status === 'pending' || r.status === 'approved') && r.requested_by_user_id === user?.id && (
                              <Button size="sm" variant="ghost" onClick={() => doCancel(r)}>Cancel</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {requests.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-500">{loading ? 'Loading...' : 'No requests found'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create Request Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>New Inventory Request</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Item Type *</Label>
                <Select value={createForm.item_type_id} onValueChange={v => setCreateForm({ ...createForm, item_type_id: v })}>
                  <SelectTrigger data-testid="req-item-type"><SelectValue placeholder="Choose item type..." /></SelectTrigger>
                  <SelectContent>{itemTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.tracking_mode})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantity *</Label>
                  <Input type="number" min="1" value={createForm.quantity} onChange={e => setCreateForm({ ...createForm, quantity: e.target.value })} data-testid="req-qty" />
                </div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={createForm.urgency} onValueChange={v => setCreateForm({ ...createForm, urgency: v })}>
                    <SelectTrigger data-testid="req-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Input value={createForm.reason} onChange={e => setCreateForm({ ...createForm, reason: e.target.value })} placeholder="e.g. New zone deployment" data-testid="req-reason" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} data-testid="req-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={submitCreate} disabled={!createForm.item_type_id} data-testid="req-submit">Submit Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Action Dialog (approve / reject / fulfill) */}
        <Dialog open={!!actionMode} onOpenChange={(open) => { if (!open) { setActionMode(null); setActing(null); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="capitalize">{actionMode} Request {acting?.request_code}</DialogTitle>
            </DialogHeader>
            {acting && (
              <div className="space-y-3 py-2">
                <div className="p-3 bg-slate-50 rounded text-sm">
                  <p><b>Item:</b> {acting.item_type_name} × {acting.quantity}</p>
                  <p><b>Requested by:</b> {acting.requested_by_name} ({acting.requested_by_role?.toUpperCase()})</p>
                  {acting.reason && <p><b>Reason:</b> {acting.reason}</p>}
                  {acting.notes && <p><b>Notes:</b> {acting.notes}</p>}
                </div>

                {actionMode === 'approve' && (
                  <div>
                    <Label>Approver Notes (optional)</Label>
                    <Input value={actionNotes} onChange={e => setActionNotes(e.target.value)} data-testid="approve-notes" />
                  </div>
                )}

                {actionMode === 'reject' && (
                  <div>
                    <Label>Reason for rejection *</Label>
                    <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-testid="reject-reason" />
                  </div>
                )}

                {actionMode === 'fulfill' && (
                  <>
                    {acting.tracking_mode === 'serialized' ? (
                      <div>
                        <Label>Pick exactly {acting.quantity} items ({selectedItemIds.size} selected)</Label>
                        <div className="border rounded max-h-60 overflow-y-auto mt-1">
                          {availableItems.length === 0 ? (
                            <p className="p-4 text-sm text-slate-500 text-center">No in-stock items of this type available</p>
                          ) : (
                            availableItems.map(it => (
                              <label key={it.id} className="flex items-center gap-2 p-2 border-b cursor-pointer hover:bg-slate-50">
                                <Checkbox checked={selectedItemIds.has(it.id)} onCheckedChange={() => toggleItem(it.id)} />
                                <span className="text-xs font-mono text-indigo-700">{it.serial_number}</span>
                                <span className="text-xs text-slate-500 ml-auto">{it.holder_name}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Label>Source Batch *</Label>
                        <Select value={selectedBatchId} onValueChange={setSelectedBatchId}>
                          <SelectTrigger data-testid="fulfill-batch"><SelectValue placeholder="Choose batch..." /></SelectTrigger>
                          <SelectContent>
                            {availableBatches.map(b => (
                              <SelectItem key={b.id} value={b.id}>
                                {b.holder_name} — {b.quantity_remaining} units remaining
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {availableBatches.length === 0 && <p className="text-xs text-red-600 mt-1">No batch has enough quantity</p>}
                      </div>
                    )}
                    <div>
                      <Label>Fulfillment Notes</Label>
                      <Input value={actionNotes} onChange={e => setActionNotes(e.target.value)} data-testid="fulfill-notes" />
                    </div>
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setActionMode(null); setActing(null); }}>Cancel</Button>
              <Button onClick={submitAction} data-testid="action-confirm" className={actionMode === 'reject' ? 'bg-red-600 hover:bg-red-700' : ''}>
                Confirm {actionMode}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default InventoryRequests;
